import { CheckCircleFilled, CloseCircleFilled, ShoppingCartOutlined } from "@ant-design/icons";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import type { AvailableItems, ChildMeta, PickerItem } from "@wms/domain";
import { Alert, App, Button, Drawer, Empty, Input, List, Segmented, Select, Space, Table, Tag, Typography } from "antd";
import dayjs from "dayjs";
import { useEffect, useState } from "react";
import { ApiError, api, errorMessage } from "../api/client";
import { useAuthStore } from "../auth/store";
import { qtyText } from "../portal/requestForm";
import { HelpHint } from "./HelpHint";
import { refreshChildLists } from "./recordQueries";
import {
  type LineOutcome,
  type Selection,
  alreadyAdded,
  availabilityWarning,
  choose,
  chosen,
  contextKey,
  conversionText,
  emptySelection,
  entryProblem,
  inContext,
  lineError,
  linePayload,
  packageOf,
  packageOption,
  pendingIds,
  previewText,
  stockChange,
  unchoose,
  updateEntry,
} from "./stockPickerModel";

const PAGE_SIZE = 20;

/** UXW-59: a line whose answer was lost keeps its outcome until it is checked — it is never sent again blindly. */
function unknownOnly(outcomes: Record<string, LineOutcome>): Record<string, LineOutcome> {
  return Object.fromEntries(Object.entries(outcomes).filter(([, o]) => !o.ok && o.unknown));
}

type Stage = "pick" | "preview" | "result";

function qty(value: string, unit: string): string {
  return `${qtyText(value)} ${unit}`;
}

/** The chosen products read again (UXW-44): their current stock, zeros included, in the context of the saved order. */
async function readChosen(orderId: string, ids: string[]): Promise<Record<string, PickerItem>> {
  if (!ids.length) return {};
  const body = await api.get<AvailableItems>(`/shipment_orders/${orderId}/available-items`, {
    ids: ids.join(","),
    only_available: "false",
    limit: 200,
  });
  return Object.fromEntries(body.items.map((item) => [item.storage_object.id, item]));
}

/** Moves the eye and the focus to a field of the main form (the field ids of the antd form are the field names). */
function focusField(name: string) {
  window.setTimeout(() => {
    const element = document.getElementById(name);
    element?.scrollIntoView({ behavior: "smooth", block: "center" });
    element?.focus();
  }, 200);
}

function AvailableCell({ item }: { item: PickerItem }) {
  const unit = item.unit.label;
  return (
    <Space direction="vertical" size={0}>
      <Typography.Text strong>{qty(item.available, unit)}</Typography.Text>
      <Typography.Text type="secondary">на складе {qty(item.quantity, unit)}</Typography.Text>
      {Number(item.reserved) > 0 && <Typography.Text type="secondary">в резерве {qty(item.reserved, unit)}</Typography.Text>}
      {Number(item.reserved_by_order) > 0 && (
        <Typography.Text type="secondary">из них этим заказом {qty(item.reserved_by_order, unit)}</Typography.Text>
      )}
      {Number(item.excluded) > 0 && (
        <Typography.Text type="warning">
          исключено {qty(item.excluded, unit)}
          <HelpHint subject="Почему исключено" text={item.excluded_reasons.join("; ")} size={12} />
        </Typography.Text>
      )}
      {Number(item.in_order) > 0 && <Tag color="blue">уже в заказе {qty(item.in_order, unit)}</Tag>}
    </Space>
  );
}

interface PickerProps {
  orderId: string;
  onClose: () => void;
  onOpenField: (name: string) => void;
}

/**
 * ТЗ «Рабочие столы» 1.1, UXW-39…46 (31.10.2026): «Добавить товары» of a shipment order — the stock of the owner of the
 * goods on the warehouse of the saved order, searched and paged by the server; the choice survives pages and
 * searches; «Добавить N товаров» shows a preview with the exact lines and units, then adds the lines one by one through
 * the ordinary «Строка заказа» create (its rights and checks), shows the outcome of each and retries only the failed
 * ones. Nothing is reserved: the plan, the reserve and the shipment are checked by the server as before.
 */
function StockPicker({ orderId, onClose, onOpenField }: PickerProps) {
  const { message } = App.useApp();
  const queryClient = useQueryClient();
  const [typed, setTyped] = useState("");
  const [q, setQ] = useState("");
  const [onlyAvailable, setOnlyAvailable] = useState(true);
  const [page, setPage] = useState(1);
  const [size, setSize] = useState(PAGE_SIZE);
  const [selection, setSelection] = useState<Selection>(() => emptySelection());
  const [showProblems, setShowProblems] = useState(false);
  const [stage, setStage] = useState<Stage>("pick");
  const [changes, setChanges] = useState<Record<string, string>>({});
  const [checking, setChecking] = useState(false);
  const [outcomes, setOutcomes] = useState<Record<string, LineOutcome>>({});
  const [before, setBefore] = useState<Record<string, string>>({});
  const [running, setRunning] = useState(false);

  // the cache key names the order, the search and the page: a late answer of another search never shows here
  const items = useQuery({
    queryKey: ["shipment_orders", orderId, "available-items", { q, onlyAvailable, page, size }],
    queryFn: () =>
      api.get<AvailableItems>(`/shipment_orders/${orderId}/available-items`, {
        q: q || undefined,
        only_available: onlyAvailable ? "true" : "false",
        limit: size,
        offset: (page - 1) * size,
      }),
    retry: false,
  });
  const data = items.data;

  // UXW-42: the choice belongs to the order, its warehouse and its owner — another context starts an empty choice
  useEffect(() => {
    if (!data) return;
    const next = inContext(selection, contextKey(data));
    if (next.selection === selection) return;
    setSelection(next.selection);
    if (next.dropped) message.warning(`Склад или владелец заказа изменились — выбор (${next.dropped}) сброшен, подберите товары заново`);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  const entries = chosen(selection);
  const count = entries.length;
  const problems = entries.map((e) => entryProblem(e)).filter(Boolean).length;
  const unitOf = (item: PickerItem) => item.unit.label;

  const search = (value: string) => {
    setQ(value.trim() ? value : "");
    setPage(1);
  };

  const toPreview = async () => {
    if (problems) {
      setShowProblems(true);
      message.error("Проверьте количество у выбранных товаров: ошибки показаны в строках");
      return;
    }
    setChecking(true);
    try {
      const fresh = await readChosen(orderId, Object.keys(selection.entries));
      const found: Record<string, string> = {};
      let next = selection;
      for (const entry of entries) {
        const id = entry.item.storage_object.id;
        const change = stockChange(entry.item, fresh[id]);
        if (change) found[id] = change;
        // the next comparison starts from what is true now
        if (fresh[id]) next = { ...next, entries: { ...next.entries, [id]: { ...next.entries[id], item: fresh[id] } } };
      }
      setSelection(next);
      setChanges(found);
      setOutcomes((prev) => unknownOnly(prev));
      setStage("preview");
    } catch (e) {
      message.error(`Не удалось проверить остатки перед добавлением: ${errorMessage(e)}`);
    } finally {
      setChecking(false);
    }
  };

  const run = async () => {
    setRunning(true);
    const ids = pendingIds(selection, outcomes);
    const result: Record<string, LineOutcome> = { ...outcomes };
    const snapshot: Record<string, string> = { ...before };
    let fresh: Record<string, PickerItem>;
    try {
      fresh = await readChosen(orderId, ids);
    } catch (e) {
      message.error(`Не удалось проверить заказ перед добавлением: ${errorMessage(e)}`);
      setRunning(false);
      return;
    }
    for (const id of ids) {
      const entry = selection.entries[id];
      const previous = result[id];
      // UXW-59: a lost answer is checked first — the line that is already in the order is not added twice
      if (previous && !previous.ok && previous.unknown && snapshot[id] !== undefined && alreadyAdded(snapshot[id], entry, fresh[id])) {
        result[id] = { ok: true, message: `Уже добавлено: ${previewText(entry)}` };
        continue;
      }
      snapshot[id] = fresh[id]?.in_order ?? entry.item.in_order;
      try {
        await api.post("/shipment_order_lines", linePayload(orderId, entry));
        result[id] = { ok: true, message: `Добавлено: ${previewText(entry)}` };
      } catch (e) {
        if (e instanceof ApiError) {
          result[id] = { ok: false, message: lineError(entry, e.problem.field, e.problem.detail ?? e.problem.title) };
        } else {
          result[id] = { ok: false, unknown: true, message: lineError(entry, null, `ответ сервера не получен (${errorMessage(e)}). Перед повтором проверим, добавилась ли строка`) };
        }
      }
      setOutcomes({ ...result });
    }
    setBefore(snapshot);
    setOutcomes(result);
    setRunning(false);
    setStage("result");
    // CW31-B11: the «Товары» tab, the steps of the order and «уже в заказе» show the new lines at once; the header
    // form is not reloaded — a value typed there and not saved yet stays
    void refreshChildLists(queryClient, "shipment_orders", orderId);
    void queryClient.invalidateQueries({ queryKey: ["shipment_orders", orderId, "workflow"] });
    void queryClient.invalidateQueries({ queryKey: ["shipment_orders", orderId, "available-items"] });
    const added = Object.values(result).filter((r) => r.ok).length;
    if (added === Object.keys(selection.entries).length) message.success(`Добавлено строк: ${added}`);
  };

  const finish = () => {
    setSelection(emptySelection(selection.context));
    onClose();
  };

  // UXW-45: back to the choice after a partial result — the added products leave the choice, so nothing is added twice
  const editChoice = () => {
    setSelection((s) => Object.keys(outcomes).reduce((acc, id) => (outcomes[id].ok ? unchoose(acc, id) : acc), s));
    setOutcomes(unknownOnly(outcomes));
    setStage("pick");
  };

  const failed = Object.values(outcomes).filter((o) => !o.ok).length;
  const title = data ? `Добавить товары в ${data.order.number}` : "Добавить товары";
  const error = items.error instanceof ApiError ? items.error.problem : null;

  let body: React.ReactNode;
  if (items.isError && error?.status === 422 && error.field === "owner_id") {
    // UXW-40: no owner in the saved order — say what to fill and where; the catalogue of all clients is not opened
    body = (
      <Alert
        type="warning"
        showIcon
        data-testid="stock-picker-no-owner"
        message={error.detail}
        description={
          <span>
            Подбор показывает остатки владельца товара на складе заказа. Выберите «Владелец товара» на вкладке «Основное»,
            сохраните заказ и откройте подбор снова. Получатель доставки владельца не заменяет. Добавить строку без подбора
            можно прежним способом — кнопкой «Создать».
          </span>
        }
        action={
          <Button
            type="primary"
            data-testid="stock-picker-owner-field"
            onClick={() => {
              onClose();
              onOpenField("owner_id");
            }}
          >
            Перейти к полю
          </Button>
        }
      />
    );
  } else if (items.isError) {
    const refused = error?.status === 403 || error?.status === 404;
    body = (
      <Alert
        type={refused ? "warning" : "error"}
        showIcon
        data-testid="stock-picker-error"
        message={refused ? "Подбор недоступен" : "Не удалось загрузить остатки"}
        description={errorMessage(items.error)}
        action={
          refused ? undefined : (
            <Button size="small" onClick={() => items.refetch()}>
              Повторить
            </Button>
          )
        }
      />
    );
  } else if (stage === "preview" || stage === "result") {
    body = (
      <Space direction="vertical" style={{ width: "100%" }} size={12}>
        <Alert
          type="info"
          showIcon
          message={stage === "preview" ? `Будет добавлено строк: ${count}` : failed ? `Добавлено не всё: ошибок ${failed}` : `Добавлено строк: ${count}`}
          description="Это плановые строки заказа: товар не резервируется и не списывается. Резерв и отгрузку сервер проверит на следующих шагах."
        />
        <List
          bordered
          data-testid="stock-picker-preview"
          dataSource={entries}
          renderItem={(entry) => {
            const id = entry.item.storage_object.id;
            const outcome = outcomes[id];
            const warning = availabilityWarning(entry);
            return (
              <List.Item data-testid="stock-picker-result" data-ok={outcome ? String(outcome.ok) : undefined}>
                <Space direction="vertical" size={2}>
                  <span>
                    {outcome ? (
                      outcome.ok ? (
                        <CheckCircleFilled style={{ color: "var(--ant-color-success, #52c41a)", marginRight: 6 }} />
                      ) : (
                        <CloseCircleFilled style={{ color: "var(--ant-color-error, #ff4d4f)", marginRight: 6 }} />
                      )
                    ) : null}
                    {outcome ? outcome.message : previewText(entry)}
                  </span>
                  {!outcome?.ok && changes[id] && <Typography.Text type="warning">{changes[id]}</Typography.Text>}
                  {!outcome && warning && <Typography.Text type="warning">{warning}</Typography.Text>}
                </Space>
              </List.Item>
            );
          }}
        />
      </Space>
    );
  } else {
    body = (
      <Space direction="vertical" style={{ width: "100%" }} size={12}>
        {data && (
          <Space wrap size={[6, 6]} data-testid="stock-picker-context">
            <Tag color="purple">Чей товар: {data.owner.name}</Tag>
            <Tag color="blue">Склад {data.warehouse.code ?? data.warehouse.name}</Tag>
            <Tag>Данные на {dayjs(data.generated_at).format("HH:mm")}</Tag>
          </Space>
        )}
        <Typography.Text type="secondary">
          Показаны остатки владельца товара на складе заказа. Общий товар каталога — только остаток этого владельца.
          Получатель доставки не подменяет владельца. Добавление не резервирует товар.
        </Typography.Text>
        <Space wrap style={{ width: "100%" }}>
          <Input.Search
            allowClear
            autoFocus
            placeholder="Название, артикул, код или штрихкод"
            style={{ width: 420, maxWidth: "calc(100vw - 64px)" }}
            value={typed}
            onChange={(e) => {
              setTyped(e.target.value);
              if (!e.target.value) search("");
            }}
            // UXW-36: Enter searches here and never saves the order; a scanned code stays a string
            onSearch={search}
            data-testid="stock-picker-search"
          />
          <Segmented
            value={onlyAvailable ? "available" : "all"}
            onChange={(v) => {
              setOnlyAvailable(v === "available");
              setPage(1);
            }}
            options={[
              { label: "Только в наличии", value: "available" },
              { label: "Все товары", value: "all" },
            ]}
            data-testid="stock-picker-filter"
          />
          <HelpHint
            subject="Только в наличии / Все товары"
            text="«Только в наличии» — что можно отобрать сейчас, и товар, который этот заказ уже зарезервировал. «Все товары» — весь каталог владельца, в том числе без остатка: строку с нехваткой можно добавить, как и раньше."
          />
        </Space>
        <Table<PickerItem>
          size="small"
          rowKey={(item) => item.storage_object.id}
          loading={items.isFetching}
          dataSource={data?.items ?? []}
          scroll={{ x: true }}
          locale={{
            emptyText: items.isSuccess ? (
              <Empty
                description={
                  q
                    ? `Ничего не найдено по «${q}» у этого владельца на этом складе`
                    : onlyAvailable
                      ? "У владельца нет доступного остатка на этом складе. Посмотрите «Все товары»"
                      : "У владельца нет товаров в каталоге и остатков на этом складе"
                }
              />
            ) : (
              " "
            ),
          }}
          pagination={{
            current: page,
            pageSize: size,
            total: data?.total ?? 0,
            showSizeChanger: true,
            showTotal: (t) => `Найдено: ${t}`,
            onChange: (p, s) => {
              setPage(s !== size ? 1 : p);
              setSize(s);
            },
          }}
          rowSelection={{
            selectedRowKeys: Object.keys(selection.entries),
            preserveSelectedRowKeys: true,
            onSelect: (item, checked) => setSelection((s) => (checked ? choose(s, item) : unchoose(s, item.storage_object.id))),
            onSelectAll: (checked, _rows, changed) =>
              setSelection((s) => changed.reduce((acc, item) => (checked ? choose(acc, item) : unchoose(acc, item.storage_object.id)), s)),
          }}
          columns={[
            {
              key: "name",
              title: "Товар",
              render: (_: unknown, item) => (
                <Space direction="vertical" size={0}>
                  <span>{item.storage_object.name}</span>
                  {item.common && (
                    <Tag color="geekblue" style={{ width: "fit-content" }}>
                      Общий каталог
                    </Tag>
                  )}
                </Space>
              ),
            },
            {
              key: "code",
              title: "Код / артикул",
              render: (_: unknown, item) => (
                <Space direction="vertical" size={0}>
                  <span>{item.storage_object.code}</span>
                  {item.storage_object.article && <Typography.Text type="secondary">арт. {item.storage_object.article}</Typography.Text>}
                </Space>
              ),
            },
            {
              key: "package",
              title: "Упаковка",
              render: (_: unknown, item) => {
                const entry = selection.entries[item.storage_object.id];
                if (!item.packages.length) return <Typography.Text type="secondary">{unitOf(item)}</Typography.Text>;
                return (
                  <Select
                    size="small"
                    style={{ minWidth: 150 }}
                    value={entry?.packageId ?? ""}
                    onChange={(v) => setSelection((s) => updateEntry(choose(s, item), item, { packageId: v || null }))}
                    options={[
                      { value: "", label: `Базовая единица (${unitOf(item)})` },
                      ...item.packages.map((p) => ({ value: p.id, label: packageOption(p, unitOf(item)) })),
                    ]}
                    aria-label={`Упаковка: ${item.storage_object.name}`}
                  />
                );
              },
            },
            { key: "available", title: "Доступно", align: "right" as const, render: (_: unknown, item) => <AvailableCell item={item} /> },
            {
              key: "amount",
              title: "В заказ",
              render: (_: unknown, item) => {
                const entry = selection.entries[item.storage_object.id];
                const pkg = entry ? packageOf(entry) : undefined;
                const problem = entry && (showProblems || entry.amount) ? entryProblem(entry) : null;
                const conversion = entry ? conversionText(entry) : null;
                const warning = entry && !problem ? availabilityWarning(entry) : null;
                return (
                  <Space direction="vertical" size={2} style={{ minWidth: 170 }}>
                    <Input
                      size="small"
                      inputMode="decimal"
                      value={entry?.amount ?? ""}
                      status={problem ? "error" : undefined}
                      addonAfter={pkg ? pkg.name : unitOf(item)}
                      placeholder="0"
                      aria-label={`В заказ: ${item.storage_object.name}, ${pkg ? `упаковок «${pkg.name}»` : unitOf(item)}`}
                      onChange={(e) => {
                        const amount = e.target.value;
                        setSelection((s) => updateEntry(choose(s, item), item, { amount }));
                      }}
                      onKeyDown={(e) => {
                        // Enter in a quantity never saves anything: the lines are added by the button below
                        if (e.key === "Enter") e.preventDefault();
                      }}
                      data-testid="stock-picker-qty"
                    />
                    {conversion && <Typography.Text type="secondary">{conversion}</Typography.Text>}
                    {problem && <Typography.Text type="danger">{problem}</Typography.Text>}
                    {warning && <Typography.Text type="warning">{warning}</Typography.Text>}
                  </Space>
                );
              },
            },
          ]}
        />
      </Space>
    );
  }

  const footer =
    stage === "pick" ? (
      <Space wrap style={{ width: "100%", justifyContent: "space-between" }}>
        <Button onClick={onClose}>Отмена</Button>
        <Space wrap>
          <Typography.Text type="secondary">Выбрано: {count}</Typography.Text>
          <Button type="primary" disabled={!count || items.isError} loading={checking} onClick={toPreview} data-testid="stock-picker-add">
            Добавить {count} {count === 1 ? "товар" : count >= 2 && count <= 4 ? "товара" : "товаров"}
          </Button>
        </Space>
      </Space>
    ) : stage === "preview" ? (
      <Space wrap style={{ width: "100%", justifyContent: "space-between" }}>
        <Button onClick={() => setStage("pick")} disabled={running} data-testid="stock-picker-back">
          Назад к выбору
        </Button>
        <Button type="primary" loading={running} onClick={run} data-testid="stock-picker-confirm">
          Добавить в заказ: {count}
        </Button>
      </Space>
    ) : (
      <Space wrap style={{ width: "100%", justifyContent: "space-between" }}>
        {failed ? (
          <Button onClick={editChoice} disabled={running} data-testid="stock-picker-edit">
            Изменить выбор
          </Button>
        ) : (
          <span />
        )}
        <Space wrap>
          {failed > 0 && (
            <Button loading={running} onClick={run} data-testid="stock-picker-retry">
              Повторить неуспешные ({failed})
            </Button>
          )}
          <Button type="primary" disabled={running} onClick={finish} data-testid="stock-picker-done">
            {failed ? "Закрыть" : "Готово"}
          </Button>
        </Space>
      </Space>
    );

  return (
    <Drawer
      open
      title={title}
      width="min(1080px, 100vw)"
      onClose={running ? undefined : onClose}
      maskClosable={!running}
      footer={footer}
      destroyOnHidden
      data-testid="stock-picker"
    >
      {body}
    </Drawer>
  );
}

/**
 * UXW-39 / UXW-46: «Добавить товары» next to «Создать» in the «Товары» tab of a shipment order — only where the meta of
 * the tab asks for it (`picker: "stock"`) and the option «Подбор товаров заказа из остатков» is on; the ordinary
 * creation of a line stays. Without the stock right the button says why it is not available.
 */
export function StockPickerButton({ child, parentId, onOpenTab }: { child: ChildMeta; parentId: string; onOpenTab?: (key: string) => void }) {
  const option = useAuthStore((s) => s.option);
  const can = useAuthStore((s) => s.can);
  const [open, setOpen] = useState(false);
  if (child.picker !== "stock" || !option("use_stock_picker") || !can("outbound.shipment_orders.write")) return null;
  const stockRight = can("inventory.stock.read");
  return (
    <>
      <Button icon={<ShoppingCartOutlined />} disabled={!stockRight} onClick={() => setOpen(true)} data-testid="stock-picker-open">
        Добавить товары
      </Button>
      {!stockRight && <Typography.Text type="secondary">Подбор из остатков — нужно право на просмотр остатков</Typography.Text>}
      {open && (
        <StockPicker
          orderId={parentId}
          onClose={() => setOpen(false)}
          onOpenField={(name) => {
            onOpenTab?.("main");
            focusField(name);
          }}
        />
      )}
    </>
  );
}
